import { useState } from 'react';
import { Link } from 'react-router-dom';
import { siteConfig, reviews, stats, faqs } from '@/mocks/siteData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9phmtmb4qjhggaepung', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg && serverMsg.includes('spam')) {
        setFormStatus('error');
        setFormError('提交失败，请稍后再试。');
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试。');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-8888',
      contactType: 'customer service',
      email: 'contact@17yiqicao.com',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Header */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
            <div className="mt-4">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">关于17一起草</h1>
              <p className="text-foreground-600 mt-2 max-w-2xl leading-relaxed">
                我们致力于打造最优质的内容创作平台，让每一个人都能轻松、高效地完成写作
              </p>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="py-10 bg-background-50 border-b border-background-200/70">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">{stat.value}</div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="lg:w-1/2">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20creative%20workspace%20with%20wooden%20desk%20and%20modern%20laptop%2C%20surrounded%20by%20books%20and%20plants%2C%20collaborative%20team%20brainstorming%20in%20soft%20focus%20background%2C%20warm%20amber%20and%20ivory%20tones%2C%20clean%20modern%20aesthetic%2C%20editorial%20photography%20with%20soft%20natural%20light&width=700&height=500&seq=about-brand&orientation=landscape"
                  alt="17一起草团队创作场景"
                  className="w-full rounded-lg"
                />
              </div>
              <div className="lg:w-1/2">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6">品牌故事</h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed">
                  <p>
                    <strong>17一起草</strong>诞生于2023年，由一群热爱写作的内容创作者共同发起。我们深知写作的孤独与困难——面对空白文档时的茫然、思路枯竭时的焦虑、反复修改却总不满意时的挫败。
                  </p>
                  <p>
                    正是这些共同的体验，让我们决定搭建一个平台，汇聚最优质的写作资源和方法论，帮助每一位创作者克服写作障碍，释放创作潜力。
                  </p>
                  <p>
                    经过三年的深耕，17一起草已发展成为拥有<strong>50万+注册用户、1万+优质文章</strong>的国内领先内容创作平台。我们的内容覆盖写作技巧、创意灵感、内容营销、学术写作、商业文案和新媒体写作六大领域，满足不同场景的写作需求。
                  </p>
                  <p>
                    我们的使命从未改变——<strong>让每一次写作都更轻松、更高效、更有价值。</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12">核心价值观</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500">
                  <i className="ri-heart-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-3">用户至上</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  每一个功能、每一篇文章，都从用户的实际需求出发。你的写作体验，是我们衡量一切的标准。
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-100/80 text-accent-500">
                  <i className="ri-star-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-3">内容为王</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  坚持原创、深度、实用的内容标准。不为流量牺牲质量，每一篇文章都经过严格审核和打磨。
                </p>
              </div>
              <div className="text-center p-6">
                <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                  <i className="ri-loop-left-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-3">持续进化</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  写作领域日新月异，我们始终保持学习心态，不断更新内容和方法论，与创作者共同成长。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-4">用户口碑</h2>
            <p className="text-foreground-600 text-center mb-12">来自真实用户的评价，是我们最宝贵的财富</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review) => (
                <div
                  key={review.name}
                  className="p-6 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < review.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5">{review.content}</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-background-200/70">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover object-top"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12">常见问题</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <details key={index} className="group rounded-lg bg-background-50 border border-background-200/70">
                  <summary className="px-6 py-4 flex items-center justify-between cursor-pointer list-none">
                    <h4 className="text-sm font-semibold text-foreground-950 pr-4">{faq.question}</h4>
                    <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                  </summary>
                  <div className="px-6 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-4">联系我们</h2>
            <p className="text-foreground-600 text-center mb-10">
              有任何问题或合作意向，欢迎随时联系我们的团队
            </p>

            {/* Contact Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
              <div className="p-5 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-md bg-primary-100/70 text-primary-500">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h4>
                <p className="text-xs text-foreground-600">contact@17yiqicao.com</p>
              </div>
              <div className="p-5 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-md bg-accent-100/80 text-accent-500">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-1">客服电话</h4>
                <p className="text-xs text-foreground-600">400-888-8888</p>
              </div>
              <div className="p-5 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-500">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-1">公司地址</h4>
                <p className="text-xs text-foreground-600">北京市朝阳区建国路88号</p>
              </div>
            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              data-readdy-form="true"
              className="rounded-lg bg-background-50 border border-background-200/70 p-6 md:p-8"
            >
              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100/80 text-accent-500">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">提交成功</h3>
                  <p className="text-sm text-foreground-600">感谢您的留言，我们会在24小时内回复您。</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {formStatus === 'error' && formError && (
                    <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                      {formError}
                    </div>
                  )}

                  {/* Honeypot */}
                  <div className="hp-field" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, overflow: 'hidden' }}>
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名 <span className="text-primary-500">*</span></label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱 <span className="text-primary-500">*</span></label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容 <span className="text-primary-500">*</span></label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i> 提交中...
                      </span>
                    ) : (
                      '提交留言'
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}